// Lets feature modules hook into `appEffects`' teardown without `appEffects` importing them.
// `appEffects` is mounted by every layout, so each module it imported statically — with its
// transport, transforms and other dependencies — was in every route's first-load JS.
//
// A module registers from its own top level, so registration happens exactly when its module
// first evaluates. One that never loaded in this tab has nothing to tear down: its in-memory
// state is still the initial state. That reasoning only holds for in-memory state — anything a
// module persists (localStorage, sessionStorage, cookies) has to be cleared whether or not the
// module loaded, so persisting stores stay direct calls in `appEffects`.

/** `endsSession`: see `resetAppState` in `appEffects`. */
type AppStateReset = (endsSession: boolean) => void;

const appStateResets = new Set<AppStateReset>();
const lastAppStateResets = new Set<AppStateReset>();
const appEffectsStops = new Set<() => void>();
const lastAppEffectsStops = new Set<() => void>();
const finalAppEffectsStops = new Set<() => void>();

/**
 * Runs on every logout / forced disconnect and when the app effects unmount. `last` resets run after the rest of
 * the teardown, including clearing the CSRF token.
 */
export function registerAppStateReset(reset: AppStateReset, { last = false }: { last?: boolean } = {}): void {
    (last ? lastAppStateResets : appStateResets).add(reset);
}

export function runAppStateResets(endsSession: boolean): void {
    for (const reset of appStateResets) reset(endsSession);
}

export function runLastAppStateResets(endsSession: boolean): void {
    for (const reset of lastAppStateResets) reset(endsSession);
}

/**
 * Runs only when the app effects unmount: before the app state reset, or after all of it with `last`. `final` runs
 * after every `last` stop, whichever module loaded first, for telemetry the rest of the teardown logs through.
 */
export function registerAppEffectsStop(
    stop: () => void,
    { last = false, final = false }: { last?: boolean; final?: boolean } = {},
): void {
    (final ? finalAppEffectsStops : last ? lastAppEffectsStops : appEffectsStops).add(stop);
}

export function runAppEffectsStops(): void {
    for (const stop of appEffectsStops) stop();
}

export function runLastAppEffectsStops(): void {
    for (const stop of lastAppEffectsStops) stop();
    for (const stop of finalAppEffectsStops) stop();
}
